import { Plus, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCredits } from "@/hooks/useCreditBalance";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Composant CreditBalance - Affichage du solde de crédits avec mises à jour en temps réel
 * 
 * Ce composant affiche:
 * - Le solde actuel de crédits avec icône et formatage
 * - Une barre de progression visuelle
 * - Un bouton "Acheter des crédits"
 * - Les mises à jour en temps réel via Supabase Realtime
 * 
 * Requirements: 6.1, 6.2, 6.5
 */
interface CreditBalanceProps {
  onBuyCredits: () => void;
  className?: string;
}

export const CreditBalance = ({ onBuyCredits, className = "" }: CreditBalanceProps) => {
  const { profile } = useAuth();
  const organizationId = profile?.organization_id;
  
  // Hook avec mises à jour en temps réel (Requirements 6.2, 6.5)
  const { availableCredits, loading, error } = useCredits(organizationId || undefined);

  // Calcul du pourcentage pour la barre de progression (max 100 crédits pour l'affichage)
  const creditPercentage = Math.min((availableCredits / 100) * 100, 100);

  // État de chargement
  if (loading) {
    return (
      <div className={`rounded-[18px] border border-white/[0.05] bg-white/[0.03] p-4 ${className}`}>
        <div className="flex items-center justify-between mb-3">
          <Skeleton className="h-4 w-16 bg-slate-600" />
          <Skeleton className="h-5 w-12 bg-slate-600" />
        </div>
        <Skeleton className="h-2 w-full mb-4 bg-slate-600" />
        <Skeleton className="h-9 w-full bg-slate-600" />
      </div>
    );
  }

  // Gestion d'erreur
  if (error) {
    return (
      <div className={`rounded-[18px] border border-white/[0.05] bg-white/[0.03] p-4 ${className}`}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-300">Crédits</span>
          <span className="text-sm font-bold text-red-400">Erreur</span>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          Impossible de charger le solde
        </p>
        <Button
          size="sm"
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-[12px] border border-white/[0.06] bg-[hsl(var(--app-sidebar-surface))] px-4 py-2.5 text-xs font-semibold text-[hsl(var(--app-sidebar-text))] hover:bg-white/[0.05]"
          onClick={onBuyCredits}
        >
          <Plus className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
          Acheter des crédits
        </Button>
      </div>
    );
  }

  // Affichage normal (Requirement 6.1)
  return (
    <div className={`rounded-[18px] border border-white/[0.05] bg-white/[0.03] p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Coins className="h-4 w-4 text-[hsl(var(--primary))]" />
          <span className="text-sm font-medium text-gray-300">Crédits</span>
        </div>
        <span className="text-sm font-bold text-white">{availableCredits}</span>
      </div>
      
      {/* Barre de progression visuelle */}
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-slate-600 mb-4">
        <div
          className="h-full transition-all duration-300 ease-in-out"
          style={{
            width: `${creditPercentage}%`,
            backgroundColor: 'hsl(var(--primary))'
          }}
        />
      </div>
      
      {/* Bouton d'achat */}
      <Button
        size="sm"
        className="inline-flex w-full items-center justify-center gap-1.5 rounded-[12px] border border-white/[0.06] bg-[hsl(var(--app-sidebar-surface))] px-4 py-2.5 text-xs font-semibold text-[hsl(var(--app-sidebar-text))] hover:bg-white/[0.05]"
        onClick={onBuyCredits}
      >
        <Plus className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
        Acheter des crédits
      </Button>
    </div>
  );
};
